/**
 * The SaaS documents' Markdown (M2-48), read into blocks a component renders
 * as ordinary elements. The API promises headings, paragraphs and bold —
 * "nothing a renderer needs HTML for" — so this reads exactly that and no
 * more: no links, no images, no raw HTML. Anything else is shown as the text
 * it is. React escapes every string, so nothing in a document can become
 * markup.
 *
 * No library: a legal document is the one place a renderer's surprise (an
 * autolinked address, a stray `_` read as emphasis) would put words on screen
 * that are not the words accepted.
 */

/** A run of text, bold or not. */
export interface Inline {
  text: string;
  bold: boolean;
}

export type Block =
  | { kind: 'heading'; level: number; inlines: Inline[] }
  | { kind: 'paragraph'; inlines: Inline[] }
  | { kind: 'list'; items: Inline[][] };

const HEADING = /^(#{1,6})\s+(.*)$/;
const ITEM = /^[-*]\s+(.*)$/;

/** `**bold**` runs; an unmatched `**` is left as text. */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let rest = text;
  while (rest.length > 0) {
    const open = rest.indexOf('**');
    const close = open === -1 ? -1 : rest.indexOf('**', open + 2);
    if (open === -1 || close === -1) {
      out.push({ text: rest, bold: false });
      break;
    }
    if (open > 0) out.push({ text: rest.slice(0, open), bold: false });
    out.push({ text: rest.slice(open + 2, close), bold: true });
    rest = rest.slice(close + 2);
  }
  return out.filter((i) => i.text.length > 0);
}

/**
 * Blocks are separated by blank lines. A block's lines are joined with a
 * space (Markdown's soft wrap), except a heading, which is its one line, and a
 * list, whose `- ` lines are its items.
 */
export function parseLegalMarkdown(source: string): Block[] {
  const blocks: Block[] = [];
  const chunks = source.replace(/\r\n?/g, '\n').split(/\n\s*\n/);
  for (const chunk of chunks) {
    const lines = chunk
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length === 0) continue;
    let paragraph: string[] = [];
    let items: string[] = [];
    const flush = () => {
      if (paragraph.length > 0)
        blocks.push({ kind: 'paragraph', inlines: parseInline(paragraph.join(' ')) });
      if (items.length > 0) blocks.push({ kind: 'list', items: items.map(parseInline) });
      paragraph = [];
      items = [];
    };
    for (const line of lines) {
      const heading = HEADING.exec(line);
      const item = ITEM.exec(line);
      if (heading) {
        flush();
        blocks.push({
          kind: 'heading',
          level: heading[1]!.length,
          inlines: parseInline(heading[2]!),
        });
      } else if (item) {
        if (paragraph.length > 0) flush();
        items.push(item[1]!);
      } else {
        if (items.length > 0) flush();
        paragraph.push(line);
      }
    }
    flush();
  }
  return blocks;
}
